import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';
import { UnassignedItem } from 'src/entities/dispatch';

export type UnassignedTooltipRow = { label: string; value: string };
export type UnassignedTooltipVM = { title: string; rows: UnassignedTooltipRow[] };

/**
 * 指定時間の解決。指定が無くても降地・積地の時間枠は運行の目安になるので順に落とす。
 */
const timeWindowOf = (item: UnassignedItem): string =>
  item.designatedTimeWindow || item.destTimeWindow || item.originTimeWindow || '指定なし';

/** 伝票の枚数。枚数が無ければ伝票一覧の数で数える */
const invoiceCountOf = (item: UnassignedItem): number =>
  item.invoiceCount ?? (item.invoices?.length ?? 0);

/**
 * 未割当ピンのホバーに出す内容。
 *
 * サンプル (route-planner components/dispatch/MapView.tsx:186-224) と同じ 6 項目。
 * 詳細カードはクリックで出す（ホバーで出すと、地図を横切るだけで開閉して読めない）。
 */
export function buildUnassignedTooltip(item: UnassignedItem): UnassignedTooltipVM {
  // 未設定を空欄にすると「取れていない」のか「0」なのか読めない
  const cases = item.cases ?? 0;
  const weight = item.weight ?? 0;
  const volume = item.volume ?? 0;
  const invoiceCount = invoiceCountOf(item);
  const time = timeWindowOf(item);
  const shippers = item.shippers ?? [];

  return {
    title: item.storeName ?? '',
    rows: [
      // 丸め方と桁区切りは他の表示と共通（metricText）。生の値を出すと
      // 浮動小数の足し算の端数がそのまま見える
      { label: '数量', value: `${quantityValueText(cases)} cs` },
      { label: '重量', value: `${weightValueText(weight)} kg` },
      { label: '体積', value: `${volumeValueText(volume)} m³` },
      { label: '伝票', value: `${invoiceCount} 枚` },
      { label: '指定時間', value: time },
      { label: '荷主', value: shippers.length > 0 ? shippers.join(', ') : '—' },
    ],
  };
}

/**
 * 1 本のピンにぶら下がる未割当案件のホバー内容。
 *
 * 地点ごとに 1 ピンへまとめたので（logposeTech/log_web_issues#2430）、ホバーには
 * 複数案件が乗りうる。1 案件ぶんの 6 行 × N 件を並べると画面外まで伸びるため、
 * 合算したサマリを 1 つだけ出す。行の並びは単独ピンと揃える（件数で行が増減すると
 * 読み手が場所を覚え直すことになる）。
 */
export function buildUnassignedPlaceTooltip(items: readonly UnassignedItem[]): UnassignedTooltipVM {
  // 1 件のときは単独ピンとまったく同じ内容にする（見え方を分岐させない）
  if (items.length === 1) return buildUnassignedTooltip(items[0]);

  const totals = items.reduce(
    (acc, item) => ({
      cases: acc.cases + (item.cases ?? 0),
      weight: acc.weight + (item.weight ?? 0),
      volume: acc.volume + (item.volume ?? 0),
      invoiceCount: acc.invoiceCount + invoiceCountOf(item),
    }),
    { cases: 0, weight: 0, volume: 0, invoiceCount: 0 },
  );

  // 割れているのに 1 つだけ出すと、その時間で全件回れると読めてしまう
  const timeWindows = new Set(items.map(timeWindowOf));
  const time = timeWindows.size === 1 ? [...timeWindows][0] : '複数';
  const shippers = [...new Set(items.flatMap((item) => item.shippers ?? []))];

  return {
    // 地点名は同じ座標にぶら下がる案件で揃っている想定だが、揃わなくても見出しは空にしない
    title: `${items[0].storeName ?? ''}（${items.length} 件）`,
    rows: [
      { label: '数量', value: `${quantityValueText(totals.cases)} cs` },
      { label: '重量', value: `${weightValueText(totals.weight)} kg` },
      { label: '体積', value: `${volumeValueText(totals.volume)} m³` },
      { label: '伝票', value: `${totals.invoiceCount} 枚` },
      { label: '指定時間', value: time },
      { label: '荷主', value: shippers.length > 0 ? shippers.join(', ') : '—' },
    ],
  };
}

/**
 * マーカーは代表 id しか持たないので、代表 id から引ける形にして渡す。
 *
 * item が引けない id（楽観削除の途中など）は数えない。引けたぶんだけで組む。
 */
export function buildUnassignedTooltipMap(
  markers: readonly { id: string; ids: readonly string[] }[],
  items: readonly UnassignedItem[],
): Map<string, UnassignedTooltipVM> {
  const itemById = new Map(items.map((item) => [item.id, item]));
  const result = new Map<string, UnassignedTooltipVM>();
  markers.forEach((marker) => {
    const placeItems = marker.ids
      .map((id) => itemById.get(id))
      .filter((item): item is UnassignedItem => item != null);
    if (placeItems.length === 0) return;
    result.set(marker.id, buildUnassignedPlaceTooltip(placeItems));
  });
  return result;
}
